import React, { useEffect, useRef, useState, createContext, useContext } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Tooltip } from '@mui/material';
import { SyncOutlined } from '@mui/icons-material';
import birdLogo from '../../../../Assets/images/Airswift Primary Logo (Gradient Bird Only).png';

import NotificationBell from '../../../common/NotificationBell/NotificationBell';
import { useIsAuthenticated } from '@azure/msal-react';

const ROUTE_META = {
  '/':                           { title: 'Home' },
  '/dashboard':                  { title: 'Dashboard' },
  '/dashboard/jobs':             { title: 'Jobs',           parent: { label: 'Dashboard', path: '/dashboard' } },
  '/dashboard/timesheets':       { title: 'Timesheets',     parent: { label: 'Dashboard', path: '/dashboard' } },
  '/dashboard/expenses':         { title: 'Expenses',       parent: { label: 'Dashboard', path: '/dashboard' } },
  '/dashboard/settings':         { title: 'Settings',       parent: { label: 'Dashboard', path: '/dashboard' } },
  '/dashboard/create-timesheet': { title: 'New Timesheet',  parent: { label: 'Timesheets', path: '/dashboard/timesheets' } },
  '/dashboard/create-claim':     { title: 'New Expense',    parent: { label: 'Expenses',   path: '/dashboard/expenses'   } },
  '/my-profile':                 { title: 'My Profile',     parent: { label: 'Dashboard', path: '/dashboard' } },
  '/onboardingInteraction':      { title: 'Onboarding' },
  '/jobs':                       { title: 'Jobs' },
};

function resolveMeta(pathname) {
  if (ROUTE_META[pathname]) return ROUTE_META[pathname];
  if (pathname.startsWith('/timesheet/')) return { title: 'Timesheet',  parent: { label: 'Timesheets', path: '/dashboard/timesheets' } };
  if (pathname.startsWith('/expense/'))  return { title: 'Expense',     parent: { label: 'Expenses',   path: '/dashboard/expenses'   } };
  if (pathname.startsWith('/jobs/'))     return { title: 'Job Detail',  parent: { label: 'Jobs',       path: '/dashboard/jobs'       } };
  return { title: 'Dashboard' };
}

export const TopBarContext = createContext({ setAction: () => {} });

export function TopBarProvider({ children }) {
  const [action, setAction] = useState(null);
  return (
    <TopBarContext.Provider value={{ action, setAction }}>
      {children}
    </TopBarContext.Provider>
  );
}

export function useTopBarAction(node) {
  const { setAction } = useContext(TopBarContext);
  useEffect(() => {
    setAction(node);
    return () => setAction(null);
  }, []);
}

function useLastUpdated() {
  const [label, setLabel] = useState('just now');
  const [since]           = useState(() => Date.now());
  useEffect(() => {
    const tick = () => {
      const s = Math.floor((Date.now() - since) / 1000);
      setLabel(s < 60 ? 'just now' : s < 120 ? '1 min ago' : `${Math.floor(s / 60)} min ago`);
    };
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [since]);
  return label;
}

export default function TopBar() {
  const location    = useLocation();
  const lastUpdated = useLastUpdated();
  const { action }  = useContext(TopBarContext);

  const meta = resolveMeta(location.pathname);
  const isAuthenticated = useIsAuthenticated();

  const [animKey, setAnimKey] = useState(0);
  const prevPath = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== prevPath.current) {
      prevPath.current = location.pathname;
      setAnimKey((k) => k + 1);
    }
  }, [location.pathname]);

  return (
    <>
      <style>{`
        @keyframes accent-in {
          from { transform: scaleY(0); opacity: 0; }
          to   { transform: scaleY(1); opacity: 1; }
        }
        .accent-in {
          transform-origin: top;
          animation: accent-in 0.3s cubic-bezier(0.4,0,0.2,1) both;
        }
      `}</style>

      <header
        className="bg-white border-b border-[#E2E8ED]"
        style={{ boxShadow: '0 1px 0 #E2E8ED' }}
      >
        <div className="flex items-center justify-between h-[56px] px-4 md:px-6 xl:px-8 gap-3 max-w-[1280px] mx-auto w-full">

          <div className="flex items-center gap-3 min-w-0">
            <img
              src={birdLogo}
              alt="Airswift"
              className="flex md:hidden h-7 w-auto object-contain shrink-0"
            />
            <div
              key={animKey}
              className="hidden md:block accent-in w-[3px] h-[28px] rounded-full bg-[#0094B3] shrink-0"
            />
            <div className="flex flex-col min-w-0">
              {meta.parent && (
                <div className="hidden md:flex items-center gap-1">
                  <Link
                    to={meta.parent.path}
                    className="text-[12px] text-[#6B7A85] hover:text-[#0094B3] no-underline font-medium transition-colors leading-none"
                  >
                    {meta.parent.label}
                  </Link>
                  <svg width="10" height="10" viewBox="0 0 20 20" stroke="currentColor" fill="none" strokeWidth="2.5" className="text-[#C4CDD5] shrink-0">
                    <path d="M7 4l6 6-6 6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="text-[12px] text-[#1A2B35] font-medium leading-none">{meta.title}</span>
                </div>
              )}
              <h1 className={`text-[15px] md:text-[16px] font-bold text-[#022D41] m-0 truncate leading-snug ${meta.parent ? 'md:mt-0.5' : ''}`}>
                {meta.title}
              </h1>
            </div>
          </div>

          {isAuthenticated && (
            <div className="flex items-center gap-2.5 md:gap-3 shrink-0">
              {action && <>{action}<div className="hidden md:block h-5 w-px bg-[#E2E8ED]" /></>}

              <Tooltip title={`Data refreshed ${lastUpdated}`} placement="bottom" arrow>
                <span className="hidden md:flex items-center gap-1.5 text-[13px] text-[#6B7A85] cursor-default select-none">
                  <SyncOutlined sx={{ fontSize: 14, color: '#6B7A85' }} />
                  {lastUpdated}
                </span>
              </Tooltip>

              <NotificationBell variant="light" />
            </div>
          )}
        </div>
      </header>
    </>
  );
}